<!--
  AdminSupportStaffModalRoot.vue

  Singleton listener for the admin fallback gate (D5/D6 of
  docs/exec-plans/completed/2026-06-23-admin-otp-gate-validateSecurityPermissionNoPrompt.md,
  mirrored from Retail POS into Restaurant POS).

  Mounted once at `src/App.vue` next to `<UserSecurityChecker />`. Listens for
  `EventBus.$emit('admin:open-support-gate', { security, resolve })` and:

    1. Renders `<AdminSupportStaffModal>` if no gate is already in-flight.
    2. On `verified({ username })` → `supportStaffGate.recordSupportAgent(username)`
       then `store.dispatch('recordlogin', username)` (audit trail) then
       settle every queued resolver as `true`.
    3. On `cancel` → settle every queued resolver as `false`.

  Concurrent invocations:
    - If a gate is already in-flight when a second call arrives, the second
      call's resolver is queued onto the same in-flight gate so we don't open
      stacked modals on a fast double-click of a gated action. All queued
      resolvers receive the same boolean when the modal settles.
-->
<template>
  <div class="admin-support-staff-modal-root">
    <AdminSupportStaffModal
      v-if="isOpen"
      :security="activeSecurity"
      @verified="onVerified"
      @cancel="onCancel"
    />
  </div>
</template>

<script>
import AdminSupportStaffModal from './AdminSupportStaffModal.vue';
import { supportStaffGate } from '@/services/supportStaffGate';
import store from '@/store';

export default {
  name: 'AdminSupportStaffModalRoot',
  components: { AdminSupportStaffModal },
  data() {
    return {
      isOpen: false,
      activeSecurity: '',
      // True while a gate is open or settling. Used to coalesce concurrent
      // invocations into a single modal session.
      inFlight: false,
      // Queued resolve() callbacks for callers waiting on the in-flight gate.
      // All get settled with the same boolean when the modal closes.
      pendingResolvers: [],
    };
  },
  created() {
    // Listen on the global EventBus set up in src/services/GlobalVariables.js.
    if (window.EventBus) {
      window.EventBus.$on('admin:open-support-gate', this.handleOpen);
      console.log('[AdminSupportStaffModalRoot] EventBus listener registered for admin:open-support-gate');
    } else {
      console.warn('[AdminSupportStaffModalRoot] window.EventBus is not available — gate will not work');
    }
  },
  beforeDestroy() {
    window.EventBus.$off('admin:open-support-gate', this.handleOpen);
  },
  methods: {
    /**
     * Handler invoked by `EventBus.$emit('admin:open-support-gate', { security, resolve })`
     * from `UserSecurityChecker.vue#checkSecurity` when the supervisor whose
     * PIN just verified is `Admin` and no support staff is on record.
     *
     * First caller opens the modal. Subsequent concurrent callers queue
     * their resolver onto the same gate — they receive the same boolean
     * outcome when the modal settles.
     */
    handleOpen({ security, resolve }) {
      console.log('[AdminSupportStaffModalRoot] handleOpen called', {
        security,
        inFlight: this.inFlight,
        isOpen: this.isOpen,
      });
      if (!this.inFlight) {
        // First caller — open the modal.
        this.activeSecurity = security;
        this.isOpen = true;
        this.inFlight = true;
        console.log('[AdminSupportStaffModalRoot] opening modal', {
          security: this.activeSecurity,
          isOpen: this.isOpen,
        });
      }
      // All callers (first or concurrent) queue their resolver. When the
      // modal settles via onVerified/onCancel, every queued resolver
      // receives the same boolean.
      this.pendingResolvers.push(resolve);
    },

    /**
     * Called when the OTP step verifies a support staff. Records the staff
     * on the current session, fires the audit-trail `recordlogin`, and
     * settles every queued resolver as `true`.
     *
     * Per D7 of the plan, `recordlogin` fires here on the first-time
     * establishment of `localStorage.supportAgent`. Subsequent gated
     * actions in the same session short-circuit (D5) and never reach this
     * path.
     */
    onVerified({ username }) {
      console.log('[AdminSupportStaffModalRoot] onVerified', { username });
      supportStaffGate.recordSupportAgent(username);
      // Fire-and-forget audit trail — if the network is down `recordlogin`
      // itself queues offline (see `user_module.js`).
      store.dispatch('recordlogin', username).catch((err) => {
        console.warn('[AdminSupportStaffModalRoot] recordlogin dispatch failed:', err);
      });
      this._settleAll(true);
    },

    /**
     * Called when the operator cancels, presses ESC, clicks the backdrop,
     * or exceeds 3 wrong OTP attempts. Settles every queued resolver as
     * `false`.
     */
    onCancel() {
      console.log('[AdminSupportStaffModalRoot] onCancel');
      this._settleAll(false);
    },

    /**
     * Settle every queued resolver with the given boolean and reset the
     * modal state so the next call opens a fresh modal.
     *
     * State is cleared synchronously before resolving — this avoids a race
     * where a fast-arriving concurrent caller could queue onto an empty
     * resolvers list between settlement and an async .finally clear.
     */
    _settleAll(approved) {
      const resolvers = this.pendingResolvers;
      this.pendingResolvers = [];
      this.inFlight = false;
      this.isOpen = false;
      this.activeSecurity = '';
      resolvers.forEach((res) => {
        try {
          res(approved);
        } catch (err) {
          console.warn('[AdminSupportStaffModalRoot] resolver threw:', err);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-support-staff-modal-root {
  // This wrapper is invisible — only the modal renders.
  position: static;
}
</style>